/*
  Predictive Search (Suchvorschläge beim Tippen) – Die Sellerie

  Hängt sich an die Suchfelder im Header (Desktop-Suchpanel und mobiles Menü) sowie an das
  Suchfeld auf der Suchseite, fragt /search/suggest über die Section Rendering API ab
  (sections/predictive-search.liquid) und zeigt die Vorschläge direkt unter dem Suchfeld an.

  - Header: Vorschläge erscheinen im Panel. Suchseite: Vorschläge liegen als Overlay über dem Inhalt.
  - Tastatur: Pfeil hoch/runter wählt einen Vorschlag, Enter öffnet ihn, Esc schließt die Liste.
  - Ohne Auswahl sendet Enter wie gewohnt das Suchformular ab.
  - Fehlt fetch/DOMParser, passiert nichts (die normale Suche funktioniert unverändert weiter).
  - Animation (nur Header, nur ohne „Bewegung reduzieren"): Skeleton beim Laden, gestaffeltes
    Einblenden der Treffer, weiche Höhenanpassung. Die Stile dazu stehen in
    assets/predictive-search-motion.css.
  - Header-Suchpanel und mobiles Menü: eingegebener Begriff erscheint als Pille mit X zum Entfernen.
  - Volltextsuche: Artikel, Seiten und Kollektionen werden nicht nur nach Titel, sondern auch nach
    Text, Tags und Autor*in durchsucht (resources[options][fields]). Produkte durchsuchen
    ohnehin alle Felder.
*/
(function () {
  'use strict';

  if (!window.fetch || !window.DOMParser || !document.querySelectorAll) {
    return;
  }

  var MIN_CHARS = 2;
  var DEBOUNCE_MS = 250;
  var SKELETON_DELAY_MS = 120;
  var STAGGER_MAX = 9;
  var HEIGHT_MS = 240;
  var RESOURCE_TYPES = 'product,collection,article,page,query';
  var SEARCH_FIELDS = 'title,body,tag,author';
  var SECTION_ID = 'predictive-search';
  var HEADER_SELECTOR = '[data-section-type="header"]';
  var INPUT_SELECTOR =
    HEADER_SELECTOR + ' input[data-search-input], ' +
    '#MainContent form[role="search"] input[data-search-input]';
  var instanceCount = 0;

  /* Bewegung nur, wenn der/die Besucher*in sie nicht reduziert hat */
  function motionOk() {
    return !(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  function skeletonHtml() {
    var row =
      '<div class="ps__skel-row">' +
      '<span class="ps__skel-thumb"></span>' +
      '<span class="ps__skel-lines"><span class="ps__skel-line"></span>' +
      '<span class="ps__skel-line ps__skel-line--short"></span></span>' +
      '</div>';
    return '<div class="ps__skeleton" aria-hidden="true">' + row + row + row + '</div>';
  }

  function PredictiveSearch(input) {
    this.input = input;
    this.form = input.form || null;
    if (!this.form || !this.form.parentNode) {
      return;
    }

    instanceCount += 1;
    this.id = 'predictive-search-' + instanceCount;
    this.cache = {};
    this.controller = null;
    this.timer = null;
    this.skeletonTimer = null;
    this.heightAnimation = null;
    this.currentQuery = '';
    this.activeIndex = -1;
    this.overlay = !input.closest(HEADER_SELECTOR);

    var action = this.form.getAttribute('action') || '/search';
    this.endpoint = action.replace(/\/+$/, '') + '/suggest';

    this.build();
    this.bind();
    this.buildPill();
  }

  /*
    Header-Suchpanel und mobiles Menü: Ein eingegebener Begriff wird als Pille mit X angezeigt (Styling in
    assets/predictive-search-motion.css). Das Feld bleibt ein normales Eingabefeld – man kann
    weitertippen, und das X leert es wieder.
  */
  PredictiveSearch.prototype.buildPill = function () {
    var input = this.input;
    var form = this.form;

    if (this.overlay || !input.closest('[data-active-name="search"], #MobileNav') || !input.parentNode) {
      return;
    }

    var lang = (document.documentElement.lang || '').toLowerCase();
    var label = lang.indexOf('de') === 0 ? 'Suchbegriff entfernen' : 'Clear search term';
    var nativeSizing = !!(window.CSS && CSS.supports && CSS.supports('field-sizing', 'content'));

    var field = document.createElement('span');
    field.className = 'ps-field';
    input.parentNode.insertBefore(field, input);
    field.appendChild(input);

    var clear = document.createElement('button');
    clear.type = 'button';
    clear.className = 'ps__clear';
    clear.hidden = true;
    clear.setAttribute('aria-label', label);
    clear.innerHTML =
      '<svg aria-hidden="true" focusable="false" viewBox="0 0 12 12" fill="none" ' +
      'stroke="currentColor" stroke-width="1.5" stroke-linecap="round">' +
      '<path d="M1.5 1.5l9 9M10.5 1.5l-9 9"></path></svg>';
    field.appendChild(clear);

    /* Ohne field-sizing (ältere Browser): Breite des Textes per Canvas messen */
    function fitWidth(filled) {
      if (nativeSizing) {
        return;
      }
      if (!filled) {
        input.style.width = '';
        return;
      }
      var style = window.getComputedStyle(input);
      var canvas = fitWidth.canvas || (fitWidth.canvas = document.createElement('canvas'));
      var context = canvas.getContext && canvas.getContext('2d');
      if (!context) {
        return;
      }
      context.font = style.fontStyle + ' ' + style.fontWeight + ' ' + style.fontSize + ' ' + style.fontFamily;
      var spacing = parseFloat(style.letterSpacing) || 0;
      var width = context.measureText(input.value).width + spacing * input.value.length;
      input.style.width = Math.ceil(width) + 6 + 'px';
    }

    function sync() {
      var filled = input.value.length > 0;
      field.classList.toggle('is-filled', filled);
      clear.hidden = !filled;
      fitWidth(filled);
    }

    input.addEventListener('input', sync);
    input.addEventListener('focus', sync);

    clear.addEventListener('click', function () {
      input.value = '';
      input.dispatchEvent(new Event('input', { bubbles: true }));
      sync();
      input.focus();
    });

    /* Klick auf die freie Fläche neben der Pille fokussiert das Feld */
    form.addEventListener('click', function (event) {
      if (event.target === form) {
        input.focus();
      }
    });

    sync();
  };

  PredictiveSearch.prototype.build = function () {
    this.el = document.createElement('div');
    this.el.className = 'ps';
    this.el.id = this.id;
    this.el.hidden = true;
    this.el.setAttribute('aria-live', 'polite');

    /* Suchseite: Liste als Overlay über dem Inhalt (Farben wie die Seite) */
    if (this.overlay) {
      this.el.className = 'ps ps--overlay bg--primary-background color--primary-text';
      var host = this.form.parentNode;
      if (window.getComputedStyle(host).position === 'static') {
        host.classList.add('ps-host');
      }
    }

    this.form.parentNode.insertBefore(this.el, this.form.nextSibling);

    this.input.setAttribute('autocomplete', 'off');
    this.input.setAttribute('role', 'combobox');
    this.input.setAttribute('aria-autocomplete', 'list');
    this.input.setAttribute('aria-expanded', 'false');
    this.input.setAttribute('aria-controls', this.id);
  };

  PredictiveSearch.prototype.bind = function () {
    var self = this;

    this.input.addEventListener('input', function () {
      self.onInput();
    });

    this.input.addEventListener('focus', function () {
      if (
        self.el.innerHTML.trim() &&
        !self.el.querySelector('.ps__skeleton') &&
        self.input.value.trim().length >= MIN_CHARS
      ) {
        self.setOpen(true);
      }
    });

    this.input.addEventListener('keydown', function (event) {
      self.onKeydown(event);
    });

    this.form.addEventListener('submit', function () {
      self.abort();
      self.setOpen(false);
    });

    /* Overlay: Klick außerhalb schließt die Liste */
    if (this.overlay) {
      document.addEventListener('click', function (event) {
        if (self.el.hidden) {
          return;
        }
        if (self.el.contains(event.target) || self.form.contains(event.target)) {
          return;
        }
        self.setOpen(false);
      });
    }
  };

  PredictiveSearch.prototype.options = function () {
    return this.el.querySelectorAll('[role="option"]');
  };

  PredictiveSearch.prototype.setOpen = function (open) {
    this.el.hidden = !open;
    this.input.setAttribute('aria-expanded', open ? 'true' : 'false');
    if (!open) {
      clearTimeout(this.skeletonTimer);
      this.setActive(-1);
    }
  };

  PredictiveSearch.prototype.setActive = function (index) {
    var options = this.options();
    var i;

    for (i = 0; i < options.length; i += 1) {
      options[i].setAttribute('aria-selected', i === index ? 'true' : 'false');
    }

    this.activeIndex = index;

    if (index >= 0 && options[index]) {
      this.input.setAttribute('aria-activedescendant', options[index].id);
      if (options[index].scrollIntoView) {
        options[index].scrollIntoView({ block: 'nearest' });
      }
    } else {
      this.input.removeAttribute('aria-activedescendant');
    }
  };

  PredictiveSearch.prototype.clear = function () {
    clearTimeout(this.skeletonTimer);
    this.el.innerHTML = '';
    this.setOpen(false);
  };

  PredictiveSearch.prototype.abort = function () {
    clearTimeout(this.timer);
    clearTimeout(this.skeletonTimer);
    if (this.controller) {
      this.controller.abort();
      this.controller = null;
    }
  };

  PredictiveSearch.prototype.onInput = function () {
    var self = this;
    var query = this.input.value.trim();

    this.abort();

    if (query.length < MIN_CHARS) {
      this.currentQuery = '';
      this.clear();
      return;
    }

    this.timer = setTimeout(function () {
      self.search(query);
    }, DEBOUNCE_MS);
  };

  /* Skeleton: nur im Header, nur wenn das Panel noch zu ist und die Antwort etwas dauert */
  PredictiveSearch.prototype.scheduleSkeleton = function (query) {
    var self = this;

    if (this.overlay || !this.el.hidden || !motionOk()) {
      return;
    }

    clearTimeout(this.skeletonTimer);
    this.skeletonTimer = setTimeout(function () {
      if (self.currentQuery === query && self.el.hidden) {
        self.el.innerHTML = skeletonHtml();
        self.el.hidden = false;
      }
    }, SKELETON_DELAY_MS);
  };

  PredictiveSearch.prototype.search = function (query) {
    var self = this;
    var key = query.toLowerCase();

    this.currentQuery = query;

    if (Object.prototype.hasOwnProperty.call(this.cache, key)) {
      this.render(this.cache[key]);
      return;
    }

    var url =
      this.endpoint +
      '?q=' + encodeURIComponent(query) +
      '&section_id=' + SECTION_ID +
      '&resources[type]=' + RESOURCE_TYPES +
      '&resources[limit]=10' +
      '&resources[limit_scope]=each' +
      '&resources[options][unavailable_products]=last' +
      '&resources[options][fields]=' + SEARCH_FIELDS;

    var options = { headers: { Accept: 'text/html' } };
    if (window.AbortController) {
      this.controller = new AbortController();
      options.signal = this.controller.signal;
    }

    this.el.setAttribute('aria-busy', 'true');
    this.scheduleSkeleton(query);

    fetch(url, options)
      .then(function (response) {
        if (!response.ok) {
          throw new Error('Predictive search request failed: ' + response.status);
        }
        return response.text();
      })
      .then(function (text) {
        var doc = new DOMParser().parseFromString(text, 'text/html');
        var section = doc.getElementById('shopify-section-' + SECTION_ID);
        var html = section ? section.innerHTML.trim() : '';

        self.cache[key] = html;

        if (self.currentQuery === query) {
          self.render(html);
        }
      })
      .catch(function (error) {
        if (error && error.name === 'AbortError') {
          return;
        }
        self.clear();
      })
      .then(function () {
        self.el.removeAttribute('aria-busy');
      });
  };

  PredictiveSearch.prototype.render = function (html) {
    if (!html) {
      this.clear();
      return;
    }

    clearTimeout(this.skeletonTimer);

    var animate = !this.overlay && motionOk();
    var wasOpen = !this.el.hidden;
    var showedResults = wasOpen && !this.el.querySelector('.ps__skeleton');
    var heightBefore = 0;

    if (animate && wasOpen) {
      heightBefore = this.el.getBoundingClientRect().height;
      if (this.heightAnimation) {
        this.heightAnimation.cancel();
        this.heightAnimation = null;
      }
    }

    this.el.innerHTML = html;

    var options = this.options();
    var i;
    for (i = 0; i < options.length; i += 1) {
      options[i].id = this.id + '-option-' + i;
    }

    this.activeIndex = -1;
    this.input.removeAttribute('aria-activedescendant');
    this.setOpen(true);

    if (animate) {
      this.animateIn(showedResults, heightBefore);
    }
  };

  /*
    Animation nach dem Rendern:
    - Panel war zu (oder zeigte das Skeleton): Treffer blenden gestaffelt ein.
    - Panel zeigte schon Treffer (Weitertippen): nur kurzes Überblenden.
    - War das Panel offen, passt sich die Höhe weich an die neue Liste an.
  */
  PredictiveSearch.prototype.animateIn = function (showedResults, heightBefore) {
    var self = this;
    var el = this.el;
    var items = el.querySelectorAll('.ps__query, .ps__heading, .ps__link, .ps__all, .ps__empty');
    var i;

    if (showedResults) {
      el.classList.remove('ps--enter');
      el.classList.add('ps--update');
    } else {
      el.classList.remove('ps--update');
      el.classList.add('ps--enter');
      for (i = 0; i < items.length; i += 1) {
        items[i].style.setProperty('--ps-i', String(Math.min(i, STAGGER_MAX)));
      }
    }

    if (heightBefore > 0 && el.animate) {
      var heightAfter = el.getBoundingClientRect().height;

      if (Math.abs(heightAfter - heightBefore) > 2) {
        var previousOverflow = el.style.overflow;
        el.style.overflow = 'hidden';

        var animation = el.animate(
          [{ height: heightBefore + 'px' }, { height: heightAfter + 'px' }],
          { duration: HEIGHT_MS, easing: 'cubic-bezier(0.22, 0.61, 0.36, 1)' }
        );
        this.heightAnimation = animation;

        var done = function () {
          el.style.overflow = previousOverflow;
          if (self.heightAnimation === animation) {
            self.heightAnimation = null;
          }
        };
        animation.onfinish = done;
        animation.oncancel = done;
      }
    }
  };

  PredictiveSearch.prototype.onKeydown = function (event) {
    var open = !this.el.hidden;
    var options = this.options();
    var count = options.length;
    var key = event.key;

    if (key === 'Escape' || key === 'Esc') {
      if (open) {
        this.setOpen(false);
      }
      return;
    }

    if (!open || !count) {
      return;
    }

    if (key === 'ArrowDown' || key === 'Down') {
      event.preventDefault();
      this.setActive((this.activeIndex + 1) % count);
    } else if (key === 'ArrowUp' || key === 'Up') {
      event.preventDefault();
      this.setActive(this.activeIndex <= 0 ? count - 1 : this.activeIndex - 1);
    } else if (key === 'Enter' && this.activeIndex >= 0 && options[this.activeIndex]) {
      event.preventDefault();
      options[this.activeIndex].click();
    }
  };

  function init() {
    var inputs = document.querySelectorAll(INPUT_SELECTOR);
    var i;

    for (i = 0; i < inputs.length; i += 1) {
      if (!inputs[i].getAttribute('data-predictive-search-ready')) {
        inputs[i].setAttribute('data-predictive-search-ready', 'true');
        new PredictiveSearch(inputs[i]);
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Theme-Editor: Sections werden nachgeladen */
  document.addEventListener('shopify:section:load', init);
})();